import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Church, 
  Plus, 
  Trash2, 
  Eye,
  EyeOff,
  Loader2,
  X,
  Save,
  MapPin,
  Video
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminTemplesPage({ admin }) {
  const [temples, setTemples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    stream_url: '',
    image_url: '',
    description: ''
  });

  useEffect(() => {
    fetchTemples();
  }, []);

  const fetchTemples = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      const response = await axios.get(`${API}/admin/temples`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemples(response.data);
    } catch (error) {
      toast.error('Failed to load temples');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemple = async () => {
    if (!formData.name || !formData.location || !formData.stream_url) {
      toast.error('Name, location and stream URL are required');
      return;
    }

    try {
      setActionLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      const params = new URLSearchParams({
        name: formData.name,
        location: formData.location,
        stream_url: formData.stream_url
      });
      if (formData.image_url) params.append('image_url', formData.image_url);
      if (formData.description) params.append('description', formData.description);

      await axios.post(`${API}/admin/temples?${params}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Temple added successfully');
      setShowForm(false);
      setFormData({ name: '', location: '', stream_url: '', image_url: '', description: '' });
      fetchTemples();
    } catch (error) {
      toast.error('Failed to add temple');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggle = async (templeId, currentStatus) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      await axios.put(`${API}/admin/temples/${templeId}/toggle?is_active=${!currentStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentStatus ? 'Temple disabled' : 'Temple enabled');
      fetchTemples();
    } catch (error) {
      toast.error('Failed to update temple');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (templeId) => {
    if (!window.confirm('Are you sure you want to delete this temple?')) {
      return;
    }

    try {
      setActionLoading(true);
      const token = localStorage.getItem('dharmbook_admin_token');
      await axios.delete(`${API}/admin/temples/${templeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Temple deleted');
      fetchTemples();
    } catch (error) {
      toast.error('Failed to delete temple');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Temple Management</h1>
          <p className="text-slate-500">Manage live aarti streams from temples</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
        >
          <Plus className="w-5 h-5" />
          Add Temple
        </button>
      </div>

      {/* Temples Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : temples.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <Church className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No temples added yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {temples.map((temple) => (
            <div
              key={temple.id}
              className={`bg-white rounded-xl border overflow-hidden ${
                !temple.is_active ? 'border-slate-300 opacity-60' : 'border-slate-200'
              }`}
            >
              {/* Image */}
              <div className="h-40 bg-gradient-to-br from-orange-100 to-amber-100 relative">
                {temple.image_url ? (
                  <img src={temple.image_url} alt={temple.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Church className="w-16 h-16 text-amber-300" />
                  </div>
                )}
                {!temple.is_active && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <span className="px-3 py-1 bg-red-500 text-white text-sm rounded-full">Disabled</span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="font-semibold text-slate-900">{temple.name}</h3>
                <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                  <MapPin className="w-4 h-4" />
                  {temple.location}
                </div>
                {temple.description && (
                  <p className="text-sm text-slate-600 mt-2 line-clamp-2">{temple.description}</p>
                )}
                <div className="flex items-center gap-1 text-xs text-slate-400 mt-2">
                  <Video className="w-3 h-3" />
                  <span className="truncate">{temple.stream_url}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
                <button
                  onClick={() => handleToggle(temple.id, temple.is_active)}
                  disabled={actionLoading}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
                    temple.is_active
                      ? 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                      : 'bg-green-50 text-green-700 hover:bg-green-100'
                  }`}
                >
                  {temple.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {temple.is_active ? 'Disable' : 'Enable'}
                </button>
                <button
                  onClick={() => handleDelete(temple.id)}
                  disabled={actionLoading}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Temple Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Add Temple</h2>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Temple Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  placeholder="e.g., Varanasi Kashi Vishwanath"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Location *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  placeholder="e.g., Varanasi, Uttar Pradesh"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Live Stream URL *</label>
                <input
                  type="url"
                  value={formData.stream_url}
                  onChange={(e) => setFormData({ ...formData, stream_url: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  placeholder="https://youtube.com/..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Image URL</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  rows={2}
                  placeholder="Brief description..."
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTemple}
                disabled={actionLoading}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Add Temple
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
